
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";

import AuditControl from "@/components/media/AuditControl.vue";
import { FeaturesList } from "@/store/types";
import { EventDetails, EventRecord } from "@/store/site-monitor/types";
import { get } from "lodash";
import { audit } from "rxjs/operators";
import { AuditService, MediaFile } from "@/services/auditService";
import api from "@/services/api.service";
import {
	MatrixStoredFile,
	StoredFileType,
	MatrixCamera,
	MatrixClip,
	MatrixContentItem
} from "@/store/site-monitor-cameras/types";
import { isActiveCell, PredefinedTour } from "@/store/tours/types";
import { Pref } from "@/store/settings/types";
import { state } from "@/store/responses";

/* import { AuditService, MediaFile } from "@/services/auditService"; */
// @note Breaking Vue reactivity when not using this.$set with
// nonprimitive data may cause errors & nasty bugs

const SiteMonitor = namespace("siteMonitor");
const SiteMonitorCameras = namespace("siteMonitorCameras");
const SessionStore = namespace("sessions");
const Tours = namespace("tours");
const SettingsStore = namespace("settings");

@Component({
	components: {
		"audit-control": AuditControl
	}
})
export default class MatrixContainer extends Vue {
	constructor() {
		super();

		this.created = Date.now().toString();
	}

	$refs!: {
		cameraControl: any;
		player: AuditControl;
		childElement: HTMLDivElement;
	};

	@Prop(Number) index?: any;

	@Getter("getFeaturesList") private featuresList: FeaturesList;

	@SiteMonitorCameras.Getter("getLayoutIndex") private layoutIndex: number;
	@SiteMonitorCameras.Getter("getAwaitingCamera") private awaitingCamera: MatrixCamera;
	@SiteMonitorCameras.Getter("getAwaitingClip") private awaitingClip: MatrixClip;
	@SiteMonitorCameras.Getter("getAwaitingStoredFile") private awaitingStoredFile: MatrixStoredFile;
	@SiteMonitorCameras.Getter("getLinkRecord") private linkRecord: EventRecord;
	@SiteMonitorCameras.Getter("getHighlightCellIndex") private highlightCellIndex: number | null;
	@SiteMonitorCameras.Getter("getWaitingForEvents") waitingForEvent: any;
	@SiteMonitorCameras.Getter private getMatrixPushContents: (index: number) => MatrixContentItem;
	@SiteMonitorCameras.Getter private getMatrixContents: (index: number) => MatrixContentItem;

	@SiteMonitor.Getter("groupID") siteGroupId: number;

	@SiteMonitorCameras.Action private updateMediaMatrixContents: ({
		index,
		eventID,
		contents,
		isController
	}) => Promise<void>;
	@SiteMonitorCameras.Mutation private setAwaitingCamera: (camera: MatrixCamera) => void;
	@SiteMonitorCameras.Mutation private setAwaitingClip: (clip: MatrixClip) => void;
	@SiteMonitorCameras.Mutation private setAwaitingStoredFile: (storedFile: MatrixStoredFile) => void;
	@SiteMonitorCameras.Mutation private setPushContents: ({ index, newContents }) => void;
	@SiteMonitorCameras.Mutation private setLinkRecord: (linkRecord: EventRecord) => void;
	@SiteMonitorCameras.Mutation private setCameraLink: any;

	@SessionStore.Getter private getSession: (resourceId: string | number) => string;
	@SessionStore.Action private updateSession: ({ resourceId, eventId }) => Promise<void>;

	@SiteMonitor.Getter("getIsController") private isController: boolean;
	@SiteMonitor.Getter("getAuditService") private auditService: AuditService;
	@SiteMonitor.Getter("getEventDetails") private eventDetails: EventDetails;

	@SiteMonitor.Mutation private setActivity: () => void;

	@Tours.Getter private tourInProgress: boolean;
	@Tours.Getter private getActiveCell: isActiveCell;
	@Tours.Getter private getPredefinedTour: PredefinedTour;

	public created: string;

	public _camera: any = null;
	public deviceId: number = 0;
	public deviceTitle: string | null = null;

	public _clip: any;
	public clipDetails: any = {};
	public hasClip: boolean = false;

	public _storedFile: any;
	public storedFileDetails: any = {};

	public highlight: boolean = false;

	private streamingData: boolean = false;
	private isActive: boolean = false;
	private imageSource: any = "";

	/**
	 * Returns the camera currently in this matrix cell, if any.
	 */
	private get camera() {
		return this._camera;
	}

	/**
	 * Sets the camera currently in this matrix cell.
	 */
	private set camera(value: any) {
		if (value == undefined) {
			this._camera = null;
			this.deviceId = -2;
			this.deviceTitle = null;
		} else {
			if (value == null) {
				this.deviceId = -1;
				this.deviceTitle = null;
			} else {
				this.deviceId = value.objectID;
				this.deviceTitle = value.title;
			}

			this._camera = value;
		}
	}

	/**
	 * Gets the clip currently in this matrix cell, if any.
	 */
	private get clip() {
		return this._clip;
	}

	/**
	 * Sets the clip currently in this matrix cell.
	 */
	private set clip(value: any) {
		if (value == null) {
			this.hasClip = false;
			this.clipDetails = {};
		} else {
			this.hasClip = true;
			this.clipDetails = value;
		}

		this._clip = value;
	}

	private get isTourMaskInactiveCameras(): boolean {
		if (!this.tourInProgress || !this.getPredefinedTour) {
			return false;
		}
		const result = get(this.featuresList, ["Alarms", "MediaMatrix", "VideoTours", "MaskInActiveCamera"]);
		return result;
	}

	private get isCellActive() {
		if (!this.tourInProgress || this.getPredefinedTour == null) {
			return false;
		}
		if (this.getActiveCell.index == this.index && this.getActiveCell.isActive) {
			return true;
		}

		return false;
	}

	private get displayBg() {
		return {
			display: this.camera || this.clip || this.storedFileDetails ? "none" : "flex",
			border: "2px solid #dee2e6 !important",
			borderRadius: "5px",
			backgroundSize: "cover",
			backgroundImage: "url(/images/placeholder_blank.jpg)",
			zIndex: 100
		};
	}

	private get cameraBackground() {
		let style: any = {
			position: "absolute",
			top: "0px",
			left: "0px",
			right: "0px",
			bottom: "0px"
		};

		if (this.awaitingCamera) {
			style = {
				...style,
				border: "2px solid #dee2e6 !important",
				backgroundSize: "cover",
				backgroundImage: "url(/images/placeholder_blank.jpg)",
				zIndex: 99
			};
		} else if (this.highlightCellIndex == this.index) {
			style = {
				...style,
				border: "2px solid #eee !important"
			};
		} else {
			style = {
				...style,
				border: "none !important"
			};
		}

		return style;
	}

	private get auditClipStyle() {
		let style: any = { display: this.clip ? "flex" : "none" };

		if (this.highlightCellIndex == this.index) {
			style = {
				...style,
				border: "2px solid #eee !important"
			};
		}

		return style;
	}

	private get pushContents() {
		return this.getMatrixPushContents(this.index);
	}

	private get matrixContent() {
		return this.getMatrixContents(this.index);
	}

	private get hasContents() {
		return this.deviceId > 0 || this.hasClip || this.storedFileDetails?.fileName ? true : false;
	}

	private get eventId() {
		return this.eventDetails ? this.eventDetails.eventID : 0;
	}

	private get displayStyle() {
		return { display: this.awaitingCamera ? "flex" : "none" };
	}

	private get manualRaiseEnabled() {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "ManualRaise"], false);
	}

	private get manualRaiseRecordingEnabled() {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "ManualRaise", "Recording"], false);
	}

	@Watch("matrixContent")
	private onMatrixUpdated() {
		this.setContents(this.getMatrixContents(this.index));
	}

	@Watch("pushContents", { immediate: true })
	private onPushContentsChanged(contents: any, oldContents: any) {
		if (contents != null) {
			console.groupCollapsed("pushContents " + this.index);
			console.log(contents);
			console.groupEnd();

			let changed = this.setContents(contents);

			if (changed) {
				this.updateMediaMatrixContents({
					index: this.index,
					contents,
					isController: this.isController,
					eventID: this.eventId
				});
			}
		}
	}

	@Watch("waitingForEvent")
	private onWaitingForEvent(waitingForEvent: any, oldWaitingForEvent: any) {
		// if we're waiting for an event
		if (waitingForEvent == true) {
			Vue.nextTick(() => {
				// clear down any content we currently have (and stop cameras recording!)
				this.clear(false);
			});
		}
	}

	@Watch("eventId")
	private onEventChanged(eventId: number, oldEventId: number) {
		if (eventId != oldEventId) {
			if (this.clip != null) this.$refs.player.stop();
			if (this.storedFileDetails != null && this.storedFileDetails.fileType == "vid") this.$refs.player.stop();
			if (this.camera != null) this.$refs.cameraControl.stop();

			this.clip = null;
			this.camera = null;
			this.storedFileDetails = null;
			this.imageSource = null;
		}
	}

	public async mounted() {
		// Clear any existing data waiting to be put in this cell
		this.setPushContents({
			index: this.index,
			newContents: null
		});
	}

	private startAuditPlayer(eventRecordID: number, clip: any, eventID: number = -1) {
		if (!this.auditService) {
			api.addErrorToSystemLog(
				this.eventId,
				"Media Matrix",
				`Audit service is null. Endpoint: ${this.eventDetails?.auditEndpoint ??
					"Unknown, event details have not been loaded."}.`
			);

			// If the audit service is null, create a $watch that waits until it's set
			const unwatch = this.$watch("auditService", (auditService: AuditService) => {
				// If we have an audit service instance, begin playing the clip, and remove the $watch
				if (auditService) {
					this.$refs.player.playClip(auditService, eventRecordID, clip, eventID);
					unwatch();
				}
			});
		} else {
			this.$refs.player.playClip(this.auditService, eventRecordID, clip, eventID);
		}
	}

	private mapStoredFileToMedia(storedFile: any): MediaFile {
		const media: MediaFile = {
			DeviceId: null,
			DeviceTitle: storedFile.fileName,
			FileIdentifier: storedFile.fileLocation,
			FriendlyStartTime: "00:00:01 01/01/0001",
			MediaType: "RAW DATA",
			StartTime: "/Date(-2211753599)/", //Need start time for snapshots,
			EventRecordId: null
		};
		return media;
	}

	private async setContents(contents: MatrixContentItem): Promise<boolean> {
		let changed = false;

		if (contents) {
			if (contents.storedFile) {
				if (this.hasClip) {
					this.$refs.player.stop();
					this.$set(this, "clip", null);
				}

				if (this.deviceId > 0) {
					this.$set(this, "camera", null);
					this.$refs.cameraControl.stop();
				}

				this.$set(this, "storedFileDetails", contents.storedFile);

				if (contents.storedFile.fileType == "vid") {
					this.startAuditPlayer(
						contents.storedFile.eventRecordId,
						this.mapStoredFileToMedia(contents.storedFile),
						this.eventId
					);
				} else if (contents.storedFile.fileType == "img") {
					const { data: imagePath } = await this.auditService.retrieveImageFromPath(
						this.getSession("2"),
						contents.storedFile.fileLocation
					);
					this.imageSource = imagePath;
				}
			} else if (contents.camera) {
				if (this.hasClip) {
					this.$refs.player.stop();
					this.$set(this, "clip", null);
				}

				if (this.storedFileDetails) {
					this.$set(this, "storedFileDetails", null);
				}

				if (this.deviceId <= 0 || this.deviceId != contents.camera.objectID) {
					changed = true;
					if (this.deviceId > 0) {
						this.$refs.cameraControl.stop();
					}

					this.$set(this, "camera", contents.camera);
					this.deviceId = contents.camera.objectID;
					this.deviceTitle = contents.camera.title;

					console.log(
						this.isController && !this.DisableVideoPref
							? "Camera recording is enabled"
							: "Camera recording is disabled"
					);

					this.$refs.cameraControl.startCamera(
						this.deviceId,
						this.eventId,
						this.isController && !this.DisableVideoPref
					);
				}
			} else if (contents.clip) {
				if (this.deviceId > 0) {
					this.$set(this, "camera", null);
					this.$refs.cameraControl.stop();
				}
				if (!this.hasClip || this.clipDetails.clip.FileIdentifier != contents.clip.clip.FileIdentifier) {
					changed = true;

					if (this.hasClip) {
						this.$refs.player.stop();
						this.$set(this, "clip", null);
					}

					this.$set(this, "clip", contents.clip);

					this.startAuditPlayer(contents.clip.eventRecordID, contents.clip.clip);
				}
			} else {
				if (this.hasClip) {
					this.$refs.player.stop();
					this.$set(this, "clip", null);
					changed = true;
				}
				if (this.storedFileDetails) {
					this.$refs.player.stop();
					this.imageSource = null;
					this.$set(this, "storedFileDetails", null);
					changed = true;
				}
				if (this.deviceId > 0) {
					this.$set(this, "camera", null);
					changed = true;
					this.$refs.cameraControl.stop();
				}
			}

			return changed;
		}

		return !!this.matrixContent;
	}

	private cameraSelected() {
		if (this.isController) {
			this.setActivity();

			if (this.hasClip) {
				this.$refs.player.stop();
				this.clip = null;
			}

			if (this.storedFileDetails) {
				this.$refs.player.stop();
				this.storedFileDetails = null;
			}

			let selectedCamera = this.awaitingCamera;

			if (selectedCamera) {
				this.setAwaitingCamera(null);
				this.deviceId = selectedCamera.objectID;

				this.updateMediaMatrixContents({
					index: this.index,
					contents: {
						camera: selectedCamera
					},
					isController: this.isController,
					eventID: this.eventId
				});

				console.log("Camera recording is " + (this.DisableVideoPref == false ? "Enabled" : "Disabled"));
				this.$refs.cameraControl.startCamera(selectedCamera.objectID, this.eventId, !this.DisableVideoPref);
				this.camera = selectedCamera;
			}
		}
	}

	private async onStoredFileSelected() {
		if (this.isController) {
			this.setActivity();

			if (this.deviceId > 0) {
				this.camera = null;
				this.$refs.cameraControl.stop();
			}

			if (this.hasClip) {
				this.$refs.player.stop();
				this.clip = null;
			}

			const selectedFile = this.awaitingStoredFile;
			this.storedFileDetails = null;

			if (selectedFile) {
				this.setAwaitingStoredFile(null);

				this.updateMediaMatrixContents({
					index: this.index,
					contents: {
						storedFile: selectedFile
					},
					isController: this.isController,
					eventID: this.eventId
				});

				this.storedFileDetails = selectedFile;
				this.$set(this, "storedFileDetails", selectedFile);
				this.imageSource = "";

				if (selectedFile.fileType == "img") {
					const { data: imagePath } = await this.auditService.retrieveImageFromPath(
						this.getSession("2"),
						selectedFile.fileLocation
					);
					this.imageSource = imagePath;
				} else if (selectedFile.fileType == "vid") {
					this.$refs.player.playClip(
						this.auditService,
						selectedFile.eventRecordId,
						this.mapStoredFileToMedia(selectedFile),
						this.eventId
					);
				}
			}
		}
	}

	private onClipSelected() {
		if (this.isController) {
			this.setActivity();

			if (this.storedFileDetails) {
				this.storedFileDetails = null;
				this.$refs.player.stop();
			}
			if (this.deviceId > 0) {
				this.camera = null;
				this.$refs.cameraControl.stop();
			}
			if (this.hasClip) {
				this.$refs.player.stop();
				this.clip = null;
			}

			let awaitingClip = this.awaitingClip;
			this.clipDetails = awaitingClip;

			this.setAwaitingClip(null);

			this.updateMediaMatrixContents({
				index: this.index,
				contents: {
					clip: awaitingClip
				},
				isController: this.isController,
				eventID: this.eventId
			});

			this.$refs.player.playClip(this.auditService, awaitingClip.eventRecordID, awaitingClip.clip);
			this.clip = awaitingClip;
		}
	}

	private clear(updateSavedContents: boolean) {
		this.$emit("contract", this.$refs.childElement);

		try {
			if (this.$refs.cameraControl != null) {
				this.$refs.cameraControl.stop();
			}
		} catch (err) {
			console.log(err);
		}

		if (this.storedFileDetails) this.storedFileDetails = null;

		if (this.imageSource != null) this.imageSource = null;

		try {
			if (this.$refs.player != null) {
				this.$refs.player.stop();
			}
		} catch (err) {
			console.log(err);
		}

		this.setContents({});
		this.updateMediaMatrixContents({
			index: this.index,
			contents: {},
			isController: this.isController,
			eventID: updateSavedContents ? this.eventId : 0
		});
	}

	private expand() {
		this.$emit("expand", this.$refs.childElement);
	}

	private makeLinkRecord() {
		this.setCameraLink({
			deviceId: this.deviceId,
			title: this.deviceTitle
		});
	}

	private get DisableVideoPref(): boolean {
		return this.eventDetails?.disableCameraRecording ?? false;
	}
}
